// Stats & Performance Calculator — reads from trade history

export const buildStats = (trades) => {
  const closed = trades.filter(t => t.result && t.result !== 'OPEN');
  if (!closed.length) return null;

  const wins   = closed.filter(t => t.result === 'WIN');
  const losses = closed.filter(t => t.result === 'LOSS');
  const bes    = closed.filter(t => t.result === 'BE');

  const totalPnl = closed.reduce((s, t) => s + (parseFloat(t.pnl_usd) || 0), 0);
  const rrValues = closed.filter(t => t.rr_achieved > 0).map(t => parseFloat(t.rr_achieved));
  const avgRR    = rrValues.length ? rrValues.reduce((a, b) => a + b, 0) / rrValues.length : 0;

  // Streak
  const sorted = [...closed].sort((a, b) => a.date_logged?.localeCompare(b.date_logged));
  let streak = 0, streakType = '';
  for (let i = sorted.length - 1; i >= 0; i--) {
    const r = sorted[i].result;
    if (i === sorted.length - 1) { streakType = r; streak = 1; }
    else if (r === streakType) streak++;
    else break;
  }

  // Best / worst trade
  const withPnl   = closed.filter(t => t.pnl_usd != null);
  const bestTrade  = withPnl.length ? withPnl.reduce((a, b) => (parseFloat(a.pnl_usd) > parseFloat(b.pnl_usd) ? a : b)) : null;
  const worstTrade = withPnl.length ? withPnl.reduce((a, b) => (parseFloat(a.pnl_usd) < parseFloat(b.pnl_usd) ? a : b)) : null;

  return {
    total: closed.length,
    wins: wins.length,
    losses: losses.length,
    bes: bes.length,
    winRate: parseFloat(((wins.length / closed.length) * 100).toFixed(1)),
    totalPnl: parseFloat(totalPnl.toFixed(2)),
    avgRR: parseFloat(avgRR.toFixed(2)),
    streak: { count: streak, type: streakType },
    bestTrade,
    worstTrade,
  };
};

const groupBy = (trades, key) => {
  const map = {};
  trades.forEach(t => {
    const k = t[key] || 'Unknown';
    if (!map[k]) map[k] = [];
    map[k].push(t);
  });
  return map;
};

const groupStats = (group) => {
  const total  = group.length;
  const wins   = group.filter(t => t.result === 'WIN').length;
  const pnl    = group.reduce((s, t) => s + (parseFloat(t.pnl_usd) || 0), 0);
  return { total, wins, winRate: total ? parseFloat(((wins / total) * 100).toFixed(0)) : 0, pnl: parseFloat(pnl.toFixed(2)) };
};

export const buildBreakdowns = (trades) => {
  const closed = trades.filter(t => t.result && t.result !== 'OPEN');

  const bySession   = groupBy(closed, 'session');
  const byDay       = groupBy(closed, 'day_of_week');
  const byGrade     = groupBy(closed, 'setup_grade');
  const byConcept   = groupBy(closed, 'ict_concept');
  const byDirection = groupBy(closed, 'direction');

  const toRows = (map) => Object.entries(map)
    .map(([label, group]) => ({ label, ...groupStats(group) }))
    .sort((a, b) => b.total - a.total);

  return {
    session:   toRows(bySession),
    day:       toRows(byDay),
    grade:     toRows(byGrade),
    concept:   toRows(byConcept),
    direction: toRows(byDirection),
  };
};

export const buildEquityCurve = (trades) => {
  const sorted = [...trades]
    .filter(t => t.result && t.result !== 'OPEN' && t.pnl_usd != null)
    .sort((a, b) => (a.date_logged || a.date)?.localeCompare(b.date_logged || b.date));

  let cumulative = 0;
  return sorted.map(t => {
    cumulative += parseFloat(t.pnl_usd) || 0;
    return { id: t.id, date: t.date, pnl: parseFloat(t.pnl_usd) || 0, cumulative: parseFloat(cumulative.toFixed(2)), result: t.result };
  });
};

export const buildPsychStats = (trades) => {
  const closed = trades.filter(t => t.result && t.result !== 'OPEN' && t.psychology_before != null);
  if (!closed.length) return null;

  const buckets = { low: [], mid: [], high: [] };
  closed.forEach(t => {
    const p = parseInt(t.psychology_before);
    if (p <= 4)      buckets.low.push(t);
    else if (p <= 7) buckets.mid.push(t);
    else             buckets.high.push(t);
  });

  return Object.entries(buckets).map(([key, group]) => ({
    label: key === 'low' ? '😰 Low (1–4)' : key === 'mid' ? '😐 Mid (5–7)' : '😊 High (8–10)',
    ...groupStats(group),
  }));
};
